import React, { useMemo } from 'react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts'
import { LAYERS } from 'config'
import { useI18n, formatIssue, formatRateSource } from 'i18n'

const StatCard = ({ label, value, hint }) => (
  <div className='rounded-xl border border-slate-200 bg-white p-4'>
    <p className='text-sm text-slate-500'>{label}</p>
    <p className='mt-1 text-2xl font-bold tabular-nums text-slate-800'>{value}</p>
    {hint && <p className='mt-1 text-xs text-slate-400'>{hint}</p>}
  </div>
)

// Tooltip: text in ink colors, the swatch carries identity.
const LayerTooltip = ({ active, payload, label, currency }) => {
  const { fmt } = useI18n()
  if (!active || !payload?.length) return null
  const rows = payload.filter((p) => p.value)
  return (
    <div className='rounded-lg border border-slate-200 bg-white p-3 text-xs shadow-sm'>
      <p className='mb-1 font-semibold text-slate-800'>{label}</p>
      {rows.map((p) => (
        <p key={p.dataKey} className='flex items-center justify-between gap-4 text-slate-600'>
          <span><span className='mr-1.5 inline-block h-2 w-2 rounded-sm' style={{ background: p.color }} />{p.name}</span>
          <span className='tabular-nums text-slate-800'>{fmt.money(p.value, currency)}</span>
        </p>
      ))}
    </div>
  )
}

const ResultTab = ({ shipment }) => {
  const { t, fmt } = useI18n()
  const result = shipment.result
  const currency = result?.reportCurrency || shipment.reportCurrency

  // Unit cost per SKU split by layer (per unit, so SKUs are comparable).
  const chartData = useMemo(() => (result?.perItem || []).map((p) => ({
    name: p.sku || p.description || `#${p.itemId}`,
    ...Object.fromEntries(LAYERS.map((l) => [l.key, p.quantity ? Math.round((p.layers[l.key] / p.quantity) * 100) / 100 : 0]))
  })), [result])
  const usedLayers = LAYERS.filter((l) => result && Number(result.totalsByLayer?.[l.key]))

  if (!result) {
    return <p className='py-8 text-center text-slate-400'>{t('result.notCalculated')}</p>
  }

  const costOverMerch = result.totalsByLayer?.merchandise
    ? result.total / result.totalsByLayer.merchandise - 1
    : null

  return (
    <div className='space-y-6'>
      {result.errors?.length > 0 && (
        <div className='rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-status-incomplete'>
          <p className='font-semibold'>{t('result.errors')}</p>
          <ul className='ml-5 list-disc'>{result.errors.map((e, i) => <li key={i}>{formatIssue(t, e)}</li>)}</ul>
        </div>
      )}
      {result.warnings?.length > 0 && (
        <div className='rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm text-status-review'>
          <p className='font-semibold'>{t('result.warnings')}</p>
          <ul className='ml-5 list-disc'>{result.warnings.map((w, i) => <li key={i}>{formatIssue(t, w)}</li>)}</ul>
        </div>
      )}

      <div className='grid grid-cols-2 gap-4 md:grid-cols-4'>
        <StatCard label={t('result.landedCost')} value={fmt.money(result.total, currency)}
          hint={t('result.calculatedAt', { date: fmt.date(shipment.calculatedAt), mode: t(`modes.${result.mode}`) })} />
        <StatCard label={t('result.overMerchandise')} value={costOverMerch === null ? '—' : `+${fmt.number(costOverMerch * 100, 1)}%`} hint={t('result.overMerchandiseHint')} />
        <StatCard label={t('result.customsValue')} value={fmt.money(result.customsValue, currency)} hint={t('result.customsValueHint')} />
        <StatCard label={t('result.recoverableVat')} value={fmt.money(result.recoverableVat, currency)} hint={t('result.recoverableVatHint')} />
      </div>

      <div className='rounded-xl border border-slate-200 bg-white p-5'>
        <h2 className='mb-1 text-sm font-semibold text-slate-700'>{t('result.chartTitle', { currency })}</h2>
        <p className='mb-4 text-xs text-slate-500'>{t('result.chartSubtitle')}</p>
        <div style={{ width: '100%', height: Math.max(220, chartData.length * 44 + 80) }}>
          <ResponsiveContainer>
            <BarChart data={chartData} layout='vertical' margin={{ top: 4, right: 24, left: 8, bottom: 4 }} barCategoryGap='28%'>
              <CartesianGrid strokeDasharray='3 3' horizontal={false} stroke='#e2e8f0' />
              <XAxis type='number' tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={(v) => fmt.number(v, 0)} />
              <YAxis type='category' dataKey='name' width={110} tick={{ fontSize: 12, fill: '#334155' }} />
              <Tooltip content={<LayerTooltip currency={currency} />} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#475569' }} iconType='square' />
              {usedLayers.map((l, i) => (
                <Bar key={l.key} dataKey={l.key} name={t(`layers.${l.key}`)} stackId='cost' fill={l.color} stroke='#ffffff' strokeWidth={2}
                  radius={i === usedLayers.length - 1 ? [0, 4, 4, 0] : 0} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className='overflow-x-auto rounded-xl border border-slate-200 bg-white'>
        <table className='min-w-full divide-y divide-slate-200 text-sm'>
          <thead className='bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500'>
            <tr>
              <th className='px-3 py-3'>{t('result.sku')}</th>
              <th className='px-3 py-3 text-right'>{t('result.qty')}</th>
              {usedLayers.map((l) => <th key={l.key} className='px-3 py-3 text-right'>{t(`layers.${l.key}`)}</th>)}
              <th className='px-3 py-3 text-right'>{t('result.total')}</th>
              <th className='px-3 py-3 text-right'>{t('result.unitCost')}</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {result.perItem.map((p, i) => (
              <tr key={p.itemId || i}>
                <td className='px-3 py-2'>
                  <p className='font-medium'>{p.sku || '—'}</p>
                  <p className='max-w-[220px] truncate text-xs text-slate-400'>{p.description}</p>
                </td>
                <td className='px-3 py-2 text-right tabular-nums'>{fmt.number(p.quantity)}</td>
                {usedLayers.map((l) => <td key={l.key} className='px-3 py-2 text-right tabular-nums text-slate-600'>{fmt.money(p.layers[l.key])}</td>)}
                <td className='px-3 py-2 text-right font-semibold tabular-nums'>{fmt.money(p.total)}</td>
                <td className='px-3 py-2 text-right font-semibold tabular-nums text-brand'>{p.unitCost === null ? '—' : fmt.number(p.unitCost, 4)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className='bg-slate-50 font-semibold'>
            <tr>
              <td className='px-3 py-2'>{t('result.total')}</td>
              <td />
              {usedLayers.map((l) => <td key={l.key} className='px-3 py-2 text-right tabular-nums'>{fmt.money(result.totalsByLayer[l.key])}</td>)}
              <td className='px-3 py-2 text-right tabular-nums'>{fmt.money(result.total)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {result.comparison && <Comparison comparison={result.comparison} currency={currency} />}

      {result.exchangeRates && Object.keys(result.exchangeRates.rates || {}).length > 0 && (
        <div className='rounded-xl border border-slate-200 bg-white p-5 text-sm'>
          <h2 className='mb-2 font-semibold text-slate-700'>{t('result.ratesUsed')}</h2>
          <ul className='space-y-1 text-slate-600'>
            {Object.entries(result.exchangeRates.rates).map(([code, rate]) => (
              <li key={code}>
                <span className='font-semibold tabular-nums'>{code} {fmt.number(rate)}</span>
                {result.exchangeRates.sources?.[code] && (
                  <span className='ml-2 text-xs text-slate-400'>{formatRateSource(t, fmt.day, result.exchangeRates.sources[code])}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.taxes && Object.keys(result.taxes).length > 0 && (
        <div className='rounded-xl border border-slate-200 bg-white p-5 text-sm'>
          <h2 className='mb-2 font-semibold text-slate-700'>{result.mode === 'estimate' ? t('result.taxesEstimated') : t('result.taxesActual')}</h2>
          <div className='flex flex-wrap gap-6'>
            {Object.entries(result.taxes).map(([code, amount]) => (
              <p key={code}><span className='text-slate-500'>{code}</span> <span className='font-semibold tabular-nums'>{fmt.money(amount, currency)}</span></p>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Signed amount; the sign carries meaning (+ = actual higher than estimated).
const Diff = ({ value, pct, money }) => {
  const { fmt } = useI18n()
  if (!value) return <span className='text-slate-400'>0</span>
  const sign = value > 0 ? '+' : '−'
  const abs = Math.abs(value)
  return (
    <span className={`tabular-nums font-semibold ${value > 0 ? 'text-status-incomplete' : 'text-status-ready'}`}>
      {sign}{money ? fmt.money(abs) : fmt.number(abs, 4)}
      {pct !== null && pct !== undefined && <span className='ml-1 text-xs font-normal'>({sign}{fmt.number(Math.abs(pct) * 100, 1)}%)</span>}
    </span>
  )
}

const Comparison = ({ comparison, currency }) => {
  const { t, fmt } = useI18n()
  const rows = [
    { key: 'total', label: t('result.comparison.landedCost'), d: comparison.total, strong: true },
    ...LAYERS.filter((l) => comparison.byLayer[l.key] && (comparison.byLayer[l.key].estimate || comparison.byLayer[l.key].actual))
      .map((l) => ({ key: l.key, label: t(`layers.${l.key}`), d: comparison.byLayer[l.key], color: l.color })),
    ...Object.entries(comparison.taxes || {}).map(([code, d]) => ({ key: `tax-${code}`, label: `· ${code}`, d, indent: true })),
    { key: 'cv', label: t('result.customsValue'), d: comparison.customsValue },
    { key: 'vat', label: t('result.recoverableVat'), d: comparison.recoverableVat }
  ]
  return (
    <div className='rounded-xl border border-slate-200 bg-white p-5'>
      <h2 className='text-sm font-semibold text-slate-700'>{t('result.comparison.title')} ({currency})</h2>
      <p className='mb-4 text-xs text-slate-500'>{t('result.comparison.subtitle')}</p>
      <div className='grid gap-6 xl:grid-cols-2'>
        {/* Each table gets its own wrapper: as direct grid items, Safari sizes a
            `min-w-full` table against the whole grid and it overflows the card. */}
        <div className='min-w-0 overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead className='text-left text-xs uppercase tracking-wide text-slate-500'>
              <tr>
                <th className='py-2'>{t('result.comparison.concept')}</th>
                <th className='whitespace-nowrap py-2 pl-3 text-right'>{t('result.comparison.estimate')}</th>
                <th className='whitespace-nowrap py-2 pl-3 text-right'>{t('result.comparison.actual')}</th>
                <th className='whitespace-nowrap py-2 pl-3 text-right'>{t('result.comparison.diff')}</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {rows.map((r) => (
                <tr key={r.key} className={r.strong ? 'font-semibold' : ''}>
                  <td className={`py-1.5 ${r.indent ? 'pl-4 text-slate-500' : ''}`}>
                    {r.color && <span className='mr-2 inline-block h-2.5 w-2.5 rounded-sm align-middle' style={{ background: r.color }} />}
                    {r.label}
                  </td>
                  <td className='whitespace-nowrap py-1.5 pl-3 text-right tabular-nums text-slate-600'>{fmt.money(r.d.estimate)}</td>
                  <td className='whitespace-nowrap py-1.5 pl-3 text-right tabular-nums text-slate-600'>{fmt.money(r.d.actual)}</td>
                  <td className='whitespace-nowrap py-1.5 pl-3 text-right'><Diff value={r.d.diff} pct={r.d.pct} money /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className='min-w-0 self-start overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead className='text-left text-xs uppercase tracking-wide text-slate-500'>
              <tr>
                <th className='py-2'>{t('result.comparison.unitCostTitle')}</th>
                <th className='whitespace-nowrap py-2 pl-3 text-right'>{t('result.comparison.estimate')}</th>
                <th className='whitespace-nowrap py-2 pl-3 text-right'>{t('result.comparison.actual')}</th>
                <th className='whitespace-nowrap py-2 pl-3 text-right'>{t('result.comparison.diff')}</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {comparison.perItem.map((p, i) => (
                <tr key={p.itemId || i}>
                  <td className='py-1.5 font-medium'>{p.sku || p.description}</td>
                  <td className='whitespace-nowrap py-1.5 pl-3 text-right tabular-nums text-slate-600'>{fmt.number(p.unitCost.estimate, 4)}</td>
                  <td className='whitespace-nowrap py-1.5 pl-3 text-right tabular-nums text-slate-600'>{fmt.number(p.unitCost.actual, 4)}</td>
                  <td className='whitespace-nowrap py-1.5 pl-3 text-right'><Diff value={p.unitCost.diff} pct={p.unitCost.pct} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {comparison.estimateWarnings?.length > 0 && (
        <div className='mt-4 text-xs text-slate-500'>
          <p>{t('result.comparison.estimateWarnings')}</p>
          <ul className='ml-5 list-disc'>{comparison.estimateWarnings.map((w, i) => <li key={i}>{formatIssue(t, w)}</li>)}</ul>
        </div>
      )}
    </div>
  )
}

export default ResultTab
